/*
  Article rich-text enhancements (StowPro custom).

  Three jobs, all on HTML that comes out of the Shopify rich-text editor and so
  cannot be changed from Liquid:

  1. Tables. Copies each column heading onto the body cells as `data-label` and
     marks the table `data-stacked`, which is what lets
     `component-article-content-custom.css` turn each row into a labelled card
     below 750px. A four-column comparison table is unreadable on a phone
     otherwise. Nothing here styles anything: if this script does not run, the
     CSS falls back to a horizontally scrolling table, which is worse but not
     broken.

  2. FAQ. Turns a run of `<h3>` questions under a "Frequently asked questions"
     heading into the same `<details>` accordion the vehicle model collection
     pages use (`sections/van-faq-custom.liquid`). Articles whose FAQ lives in
     the `custom.faq` metafield are already accordions, rendered by
     `snippets/article-faq-custom.liquid`; this covers the ones where the FAQ was
     hand-written into the article body instead.

     The answers stay in the DOM, so the FAQPage structured data on those
     articles still matches the visible content. Collapsed is not hidden.

  3. Contents list. Fills the "In this article" list in the sidebar
     (sections/main-article.liquid, #ArticleToc) from the article's H2s, giving
     each one an id to jump to, and marks the section being read as the page
     scrolls. Runs after the FAQ job so the FAQ heading is included. Left hidden
     when there are fewer than three headings. When the body carries its own
     contents list (a list of three or more in-page links), that list and its
     heading are tagged `article-inline-toc` and hidden from 990px, where the
     sidebar takes over; phones keep it, as they have no sidebar list.

  Runs once, on DOMContentLoaded or immediately if the document is already
  parsed. Safe to load on any article.
*/

(function () {
  'use strict';

  var ANSWER_TAGS = ['P', 'UL', 'OL', 'BLOCKQUOTE', 'FIGURE', 'TABLE', 'IMG', 'PRE'];
  var FAQ_HEADING = /^\s*(frequently asked questions?|faqs?)\s*[:?]?\s*$/i;

  function labelTables(root) {
    // `root` is itself the .rte element, so this must not look for a nested one.
    var tables = root.querySelectorAll('table');

    Array.prototype.forEach.call(tables, function (table) {
      if (table.hasAttribute('data-stacked')) return;

      var headRow = table.tHead && table.tHead.rows.length ? table.tHead.rows[0] : null;
      if (!headRow) return;

      var labels = Array.prototype.map.call(headRow.cells, function (cell) {
        return (cell.textContent || '').trim();
      });
      if (labels.length < 2) return;

      var bodyRows = table.querySelectorAll('tbody tr');
      if (!bodyRows.length) return;

      // A colspan anywhere means the cells no longer line up with the headings,
      // and a mislabelled card is worse than a scrolling table. Leave it alone.
      var alignable = Array.prototype.every.call(bodyRows, function (row) {
        return row.cells.length === labels.length;
      });
      if (!alignable) return;

      Array.prototype.forEach.call(bodyRows, function (row) {
        Array.prototype.forEach.call(row.cells, function (cell, index) {
          if (labels[index]) cell.setAttribute('data-label', labels[index]);
        });
      });

      table.setAttribute('data-stacked', '');
    });
  }

  function caretFor(template) {
    if (!template || !template.content) return null;
    var caret = template.content.firstElementChild;
    return caret ? caret.cloneNode(true) : null;
  }

  function buildAccordion(question, answerNodes, index, caretTemplate) {
    var summaryId = 'ArticleFaqSummary-' + index;

    var wrapper = document.createElement('div');
    wrapper.className = 'accordion';

    var details = document.createElement('details');
    details.id = 'ArticleFaq-' + index;
    // The model collection FAQ opens its first question, so this matches it.
    if (index === 1) details.setAttribute('open', '');

    var summary = document.createElement('summary');
    summary.id = summaryId;

    var title = document.createElement('h3');
    title.className = 'accordion__title inline-richtext h4';
    while (question.firstChild) title.appendChild(question.firstChild);
    summary.appendChild(title);

    var caret = caretFor(caretTemplate);
    if (caret) summary.appendChild(caret);

    var content = document.createElement('div');
    content.className = 'accordion__content rte';
    content.setAttribute('role', 'region');
    content.setAttribute('aria-labelledby', summaryId);
    answerNodes.forEach(function (node) {
      content.appendChild(node);
    });

    details.appendChild(summary);
    details.appendChild(content);
    wrapper.appendChild(details);
    return wrapper;
  }

  function accordionFaq(root, caretTemplate) {
    var headings = root.querySelectorAll('h2');
    var faqHeading = null;

    Array.prototype.forEach.call(headings, function (heading) {
      if (faqHeading) return;
      if (heading.id === 'faq' || FAQ_HEADING.test(heading.textContent || '')) {
        faqHeading = heading;
      }
    });

    if (!faqHeading) return;

    // Collect the question/answer runs before touching the DOM, so a malformed
    // section is detected and skipped rather than half-converted.
    var items = [];
    var current = null;
    var node = faqHeading.nextElementSibling;

    while (node) {
      if (node.tagName === 'H3') {
        current = { question: node, answer: [] };
        items.push(current);
      } else if (current && ANSWER_TAGS.indexOf(node.tagName) !== -1) {
        current.answer.push(node);
      } else {
        // A heading, a call-to-action div or the JSON-LD block: the FAQ has ended.
        break;
      }
      node = node.nextElementSibling;
    }

    var usable = items.filter(function (item) {
      return item.answer.length > 0;
    });
    if (usable.length !== items.length || usable.length === 0) return;

    var list = document.createElement('div');
    list.className = 'article-faq article-faq--inline';

    faqHeading.classList.add('article-faq__heading');
    faqHeading.parentNode.insertBefore(list, faqHeading.nextSibling);

    usable.forEach(function (item, i) {
      list.appendChild(buildAccordion(item.question, item.answer, i + 1, caretTemplate));
      item.question.parentNode.removeChild(item.question);
    });
  }

  function slugify(text) {
    return (text || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  // The first top-level list in the body whose links are all in-page and number
  // three or more, plus the heading directly above it if there is one.
  function findInlineToc(root) {
    var lists = root.querySelectorAll(':scope > ul, :scope > ol');
    for (var i = 0; i < lists.length; i++) {
      var anchors = lists[i].querySelectorAll('a');
      if (anchors.length < 3) continue;
      var allLocal = Array.prototype.every.call(anchors, function (a) {
        return (a.getAttribute('href') || '').charAt(0) === '#';
      });
      if (!allLocal) continue;
      var prev = lists[i].previousElementSibling;
      var heading = prev && /^H[2-4]$/.test(prev.tagName) ? prev : null;
      return { list: lists[i], heading: heading };
    }
    return null;
  }

  function buildToc(root) {
    var nav = document.getElementById('ArticleToc');
    if (!nav) return;
    var list = nav.querySelector('.article-toc__list');
    if (!list) return;

    // A contents list typed into the body (a list of three or more in-page
    // links, usually under an "In this article" heading) is kept for phones,
    // where there is no sidebar, and hidden from 990px by the CSS, where the
    // sidebar list replaces it. Its wording is reused, since the author often
    // shortens the headings there.
    var inline = findInlineToc(root);
    var labels = {};
    if (inline) {
      Array.prototype.forEach.call(inline.list.querySelectorAll('a'), function (a) {
        var target = decodeURIComponent(a.getAttribute('href').slice(1));
        var label = (a.textContent || '').trim();
        if (target && label) labels[target] = label;
      });
    }

    // Direct children only: an <h2> inside a hand-written call-to-action box is
    // not a section of the article.
    var headings = Array.prototype.filter.call(
      document.querySelectorAll('.article-template__content > h2, .article-template__faq > h2'),
      function (heading) {
        return !inline || heading !== inline.heading;
      }
    );
    if (headings.length < 3) return;

    var links = [];
    headings.forEach(function (heading) {
      var text = (heading.textContent || '').trim();
      if (!text) return;
      if (heading.id && labels[heading.id]) text = labels[heading.id];

      if (!heading.id) {
        var base = slugify(text) || 'section';
        var id = base;
        var n = 2;
        while (document.getElementById(id)) id = base + '-' + n++;
        heading.id = id;
      }

      var item = document.createElement('li');
      var link = document.createElement('a');
      link.className = 'article-toc__link';
      link.href = '#' + heading.id;
      link.textContent = text;
      item.appendChild(link);
      list.appendChild(item);
      links.push({ heading: heading, link: link });
    });

    if (links.length < 3) return;
    nav.hidden = false;
    if (inline) {
      inline.list.classList.add('article-inline-toc');
      if (inline.heading) inline.heading.classList.add('article-inline-toc');
    }

    // The section being read is the last heading above a line a third of the
    // way down the window. Recomputed once per frame at most.
    var current = null;
    var queued = false;

    function update() {
      queued = false;
      var line = window.innerHeight / 3;
      var active = null;
      for (var i = 0; i < links.length; i++) {
        if (links[i].heading.getBoundingClientRect().top <= line) active = links[i];
        else break;
      }
      if (active === current) return;
      if (current) current.link.removeAttribute('aria-current');
      if (active) active.link.setAttribute('aria-current', 'true');
      current = active;
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }

  function init() {
    var root = document.querySelector('.article-template__content');
    if (!root) return;

    labelTables(root);
    accordionFaq(root, document.getElementById('ArticleFaqCaretTemplate'));
    buildToc(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
